const KL_LIGHTBOX_SELECTOR = '[data-kl-media-lightbox]';
const KL_LIGHTBOX_OPEN_SELECTOR = '[data-kl-media-lightbox-open]';
const KL_LIGHTBOX_TRACK_SELECTOR = '[data-kl-media-lightbox-track]';
const KL_LIGHTBOX_SLIDE_SELECTOR = '[data-kl-media-lightbox-slide]';
const KL_LIGHTBOX_ZOOMABLE_ATTRIBUTE = 'data-kl-media-lightbox-zoomable';
const KL_LIGHTBOX_INDEX_SELECTOR = '[data-kl-media-lightbox-index]';
const KL_LIGHTBOX_ZOOM_SELECTOR = '[data-kl-media-lightbox-zoom]';
const KL_LIGHTBOX_CLOSE_SELECTOR = '[data-kl-media-lightbox-close]';
const KL_LIGHTBOX_PREVIOUS_SELECTOR = '[data-kl-media-lightbox-previous]';
const KL_LIGHTBOX_NEXT_SELECTOR = '[data-kl-media-lightbox-next]';
const KL_LIGHTBOX_PLAYER_SELECTOR = '[data-kl-media-lightbox-video]';
const KL_LIGHTBOX_EMBED_SELECTOR = '[data-kl-media-lightbox-embed]';

const KL_LIGHTBOX_OPEN_EVENT = 'kl-media-lightbox:open';
const KL_LIGHTBOX_ZOOMED_CLASS = 'is-zoomed';
const KL_LIGHTBOX_SCROLL_LOCK_CLASS = 'overflow-hidden';

function getSlideIndex(track) {
  if (!track.clientWidth) return 0;

  return Math.round(track.scrollLeft / track.clientWidth);
}

function clampIndex(index, slideCount, canLoop) {
  if (slideCount < 1) return 0;

  if (!canLoop) return Math.min(Math.max(index, 0), slideCount - 1);

  return (index + slideCount) % slideCount;
}

function startEmbed(slide) {
  const embed = slide.querySelector(KL_LIGHTBOX_EMBED_SELECTOR);

  if (!embed) return;

  const embedSource = embed.dataset.klMediaLightboxEmbedSrc;

  if (!embedSource || embed.getAttribute('src') === embedSource) return;

  embed.setAttribute('src', embedSource);
}

function startPlayer(slide) {
  const player = slide.querySelector(KL_LIGHTBOX_PLAYER_SELECTOR);

  if (!player) return;

  const playRequest = player.play();

  if (!playRequest || typeof playRequest.catch !== 'function') return;

  playRequest.catch(() => {
    player.muted = true;

    const mutedRequest = player.play();

    if (!mutedRequest || typeof mutedRequest.catch !== 'function') return;

    mutedRequest.catch((error) => {
      console.debug('kl-media-lightbox: the video on this slide could not be started', {
        mediaId: slide.dataset.klMediaLightboxMediaId,
        error,
      });
    });
  });
}

function startMedia(slide) {
  startPlayer(slide);
  startEmbed(slide);
}

function stopMedia(slide) {
  try {
    const player = slide.querySelector(KL_LIGHTBOX_PLAYER_SELECTOR);

    if (player) player.pause();

    const embed = slide.querySelector(KL_LIGHTBOX_EMBED_SELECTOR);

    if (embed) embed.removeAttribute('src');
  } catch (error) {
    console.debug('kl-media-lightbox: the media on this slide could not be stopped', {
      mediaId: slide.dataset.klMediaLightboxMediaId,
      error,
    });
  }
}

function initLightbox(lightbox) {
  if (lightbox.dataset.klMediaLightboxReady === 'true') return;

  const track = lightbox.querySelector(KL_LIGHTBOX_TRACK_SELECTOR);

  if (!track) {
    console.debug('kl-media-lightbox: the viewer has no track to scroll', { id: lightbox.id });
    return;
  }

  const slides = Array.from(track.querySelectorAll(KL_LIGHTBOX_SLIDE_SELECTOR));

  if (!slides.length) return;

  lightbox.dataset.klMediaLightboxReady = 'true';

  const indexOutput = lightbox.querySelector(KL_LIGHTBOX_INDEX_SELECTOR);
  const zoomButton = lightbox.querySelector(KL_LIGHTBOX_ZOOM_SELECTOR);
  const previousButton = lightbox.querySelector(KL_LIGHTBOX_PREVIOUS_SELECTOR);
  const nextButton = lightbox.querySelector(KL_LIGHTBOX_NEXT_SELECTOR);
  const canLoop = lightbox.dataset.klMediaLightboxLoop !== 'false';

  let currentIndex = 0;
  let pendingFrame = null;

  let pendingTarget = null;

  function clearZoom() {
    slides.forEach((slide) => slide.classList.remove(KL_LIGHTBOX_ZOOMED_CLASS));
    lightbox.classList.remove(KL_LIGHTBOX_ZOOMED_CLASS);

    if (zoomButton) zoomButton.setAttribute('aria-pressed', 'false');
  }

  function setZoom(isZoomed, originX, originY) {
    const slide = slides[currentIndex];

    if (!isZoomed || !slide || !slide.hasAttribute(KL_LIGHTBOX_ZOOMABLE_ATTRIBUTE)) {
      if (slide) slide.scrollTo({ left: 0, top: 0 });
      clearZoom();
      return;
    }

    slide.classList.add(KL_LIGHTBOX_ZOOMED_CLASS);
    lightbox.classList.add(KL_LIGHTBOX_ZOOMED_CLASS);

    if (zoomButton) zoomButton.setAttribute('aria-pressed', 'true');

    const bounds = slide.getBoundingClientRect();
    const ratioX = Number.isFinite(originX) ? (originX - bounds.left) / bounds.width : 0.5;
    const ratioY = Number.isFinite(originY) ? (originY - bounds.top) / bounds.height : 0.5;

    slide.scrollTo({
      left: ratioX * (slide.scrollWidth - slide.clientWidth),
      top: ratioY * (slide.scrollHeight - slide.clientHeight),
    });
  }

  function applyArrowState() {
    if (canLoop) return;

    if (previousButton) previousButton.disabled = currentIndex <= 0;
    if (nextButton) nextButton.disabled = currentIndex >= slides.length - 1;
  }

  function applyIndex(nextIndex) {
    if (nextIndex === currentIndex) return;

    const leavingSlide = slides[currentIndex];

    if (leavingSlide) stopMedia(leavingSlide);

    clearZoom();
    currentIndex = nextIndex;

    const arrivingSlide = slides[currentIndex];

    if (arrivingSlide) startMedia(arrivingSlide);

    if (indexOutput) indexOutput.textContent = String(currentIndex + 1);

    applyArrowState();
  }

  function readIndex() {
    pendingFrame = null;

    const readIndexValue = clampIndex(getSlideIndex(track), slides.length, false);

    if (pendingTarget !== null) {
      if (readIndexValue !== pendingTarget) return;

      pendingTarget = null;
    }

    applyIndex(readIndexValue);
  }

  function goToIndex(nextIndex, isImmediate) {
    const target = clampIndex(nextIndex, slides.length, canLoop);

    pendingTarget = target;
    clearZoom();

    track.scrollTo({
      left: target * track.clientWidth,
      behavior: isImmediate ? 'auto' : 'smooth',
    });

    applyIndex(target);
  }

  track.addEventListener(
    'scroll',
    () => {
      if (pendingFrame !== null) return;

      pendingFrame = requestAnimationFrame(readIndex);
    },
    { passive: true }
  );

  function releasePendingTarget() {
    pendingTarget = null;
  }

  track.addEventListener('pointerdown', releasePendingTarget, { passive: true });
  track.addEventListener('wheel', releasePendingTarget, { passive: true });

  lightbox.addEventListener('click', (event) => {
    if (event.target.closest(KL_LIGHTBOX_CLOSE_SELECTOR)) {
      lightbox.close();
      return;
    }

    if (event.target.closest(KL_LIGHTBOX_PREVIOUS_SELECTOR)) {
      goToIndex(currentIndex - 1);
      return;
    }

    if (event.target.closest(KL_LIGHTBOX_NEXT_SELECTOR)) {
      goToIndex(currentIndex + 1);
      return;
    }

    if (event.target.closest(KL_LIGHTBOX_ZOOM_SELECTOR)) {
      setZoom(!lightbox.classList.contains(KL_LIGHTBOX_ZOOMED_CLASS));
      return;
    }

    const slide = event.target.closest(KL_LIGHTBOX_SLIDE_SELECTOR);

    if (!slide || !slide.hasAttribute(KL_LIGHTBOX_ZOOMABLE_ATTRIBUTE)) return;

    setZoom(!slide.classList.contains(KL_LIGHTBOX_ZOOMED_CLASS), event.clientX, event.clientY);
  });

  lightbox.addEventListener('keydown', (event) => {
    // The arrow keys belong to a focused player, where they scrub rather than navigate.
    if (event.target.closest && event.target.closest(KL_LIGHTBOX_PLAYER_SELECTOR)) return;

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goToIndex(currentIndex + 1);
      return;
    }

    if (event.key !== 'ArrowLeft') return;

    event.preventDefault();
    goToIndex(currentIndex - 1);
  });

  lightbox.addEventListener('close', () => {
    slides.forEach(stopMedia);
    clearZoom();
    document.body.classList.remove(KL_LIGHTBOX_SCROLL_LOCK_CLASS);
  });

  lightbox.addEventListener(KL_LIGHTBOX_OPEN_EVENT, (event) => {
    const mediaId = event.detail && event.detail.mediaId;
    const requestedIndex = slides.findIndex((slide) => slide.dataset.klMediaLightboxMediaId === mediaId);

    currentIndex = -1;
    goToIndex(requestedIndex < 0 ? 0 : requestedIndex, true);
  });

  applyArrowState();
}

function openLightbox(trigger) {
  const lightbox = document.getElementById(trigger.dataset.klMediaLightboxOpen || '');

  if (!lightbox) return false;

  initLightbox(lightbox);

  try {
    lightbox.showModal();
    document.body.classList.add(KL_LIGHTBOX_SCROLL_LOCK_CLASS);
  } catch (error) {
    console.debug('kl-media-lightbox: the viewer could not be opened', { id: lightbox.id, error });
    return false;
  }

  lightbox.dispatchEvent(
    new CustomEvent(KL_LIGHTBOX_OPEN_EVENT, { detail: { mediaId: trigger.dataset.mediaId } })
  );

  return true;
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest(KL_LIGHTBOX_OPEN_SELECTOR);

  if (!trigger) return;

  if (openLightbox(trigger)) event.preventDefault();
});

document.querySelectorAll(KL_LIGHTBOX_SELECTOR).forEach(initLightbox);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll(KL_LIGHTBOX_SELECTOR).forEach(initLightbox);
});
